// AboutMe.jsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import {
  Send as SendIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { aboutService } from "../../../api/services/aboutService";
import CustomInput from "../../../components/CustomForm/CustomInput";
import CustomRichText from "../../../components/CustomForm/CustomRichText";
import CustomDateTime from "../../../components/CustomForm/CustomDateTime";
import CustomSelect from "../../FormFields/CustomSelect/CustomSelect";

const AboutMe = () => {
  const { id: clientId } = useParams();
  const [formData, setFormData] = useState({});
  const [formStructure, setFormStructure] = useState(null);
  const [formId, setFormId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [submissionId, setSubmissionId] = useState(null);

  useEffect(() => {
    const fetchActiveForm = async () => {
      try {
        const forms = await aboutService.getActiveAboutForms();
        if (forms && forms.length > 0) {
          setFormId(forms[0].id);
        }
      } catch (error) {
        console.error("Error fetching active form:", error);
      }
    };
    fetchActiveForm();
  }, []);

  const { data, isLoading, isError } = useQuery(
    ['aboutFormStructure', formId],
    () => aboutService.getAboutFormFullStructure(formId),
    {
      enabled: !!formId,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (data) {
      setFormStructure(data);
    }
  }, [data]);

  const { data: existingSubmission, refetch: refetchSubmission } = useQuery(
    ['clientAboutSubmission', clientId, formId],
    () => aboutService.getClientLatestAbout(clientId),
    {
      enabled: !!clientId && !!formId,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data && data.id) {
          setSubmissionId(data.id);
          if (data.responses) {
            setFormData(data.responses);
          }
        }
      },
    }
  );

  const handleInputChange = (fieldId, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldId]: value,
    }));
  };

  const postMutation = useMutation(
    () => {
      if (submissionId) {
        return aboutService.updateAboutSubmission(submissionId, {
          client: parseInt(clientId),
          form: parseInt(formId),
          responses: formData
        });
      } else {
        return aboutService.submitAboutForm(clientId, formId, formData);
      }
    },
    {
      onSuccess: (data) => {
        enqueueSnackbar(
          submissionId ? "Client about updated successfully" : "Client about posted successfully",
          { variant: "success" }
        );
        if (data && data.id) {
          setSubmissionId(data.id);
        }
        refetchSubmission();
      },
      onError: (error) => {
        enqueueSnackbar("Failed to save client about", { variant: "error" });
        console.error("Error:", error);
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    postMutation.mutate();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderField = (field) => {
    const value = formData[field.id] || field.field_value || "";

    switch (field.field_type) {
      case 'singleline':
        return (
          <CustomInput
            type="text"
            label={field.label}
            placeholder={field.placeholder || "Enter text"}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            fullWidth
            helperText={field.help_text || ""}
          />
        );

      case 'multiline':
        return (
          <CustomInput
            type="text"
            label={field.label}
            placeholder={field.placeholder || "Enter text"}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            fullWidth
            multiline
            rows={4}
            helperText={field.help_text || ""}
          />
        );

      case 'richtext':
        return (
          <Box>
            <Typography variant="subtitle2" fontWeight={500} sx={{ color: '#1e293b', mb: 1 }}>
              {field.label}
              {field.required && (
                <Typography component="span" sx={{ color: '#ef4444', ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>
            <CustomRichText
              value={value}
              onChange={(val) => handleInputChange(field.id, val)}
              placeholder={field.placeholder || "Write your response here..."}
            />
            {field.help_text && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {field.help_text}
              </Typography>
            )}
          </Box>
        );

      case 'select':
        const selectOptions = field.choices ? field.choices.split(',').map(opt => ({
          value: opt.trim(),
          label: opt.trim()
        })) : [];
        
        return (
          <CustomSelect
            options={selectOptions}
            label={field.label}
            placeholder={field.placeholder || "Select an option"}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            fullWidth
            helperText={field.help_text || ""}
          />
        );

      case 'number':
        return (
          <CustomInput
            type="number"
            label={field.label}
            placeholder={field.placeholder || "Enter number"}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            fullWidth
            helperText={field.help_text || ""}
          />
        );

      case 'date':
        return (
          <CustomInput
            type="date"
            label={field.label}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            fullWidth
            helperText={field.help_text || ""}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'datetime':
        return (
          <Box>
            <CustomDateTime
              label={field.label}
              value={value}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              required={field.required}
              helperText={field.help_text || ""}
              format="YYYY-MM-DD HH:mm"
            />
          </Box>
        );

      case 'checkbox':
        return (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === true || value === "true"}
                  onChange={(e) => handleInputChange(field.id, e.target.checked)}
                  sx={{
                    color: '#6366f1',
                    '&.Mui-checked': {
                      color: '#6366f1',
                    },
                  }}
                />
              }
              label={field.label}
            />
            {field.help_text && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {field.help_text}
              </Typography>
            )}
          </Box>
        );

      case 'email':
        return (
          <CustomInput
            type="email"
            label={field.label}
            placeholder={field.placeholder || "Enter email"}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            fullWidth
            helperText={field.help_text || ""}
          />
        );

      case 'phone':
        return (
          <CustomInput
            type="tel"
            label={field.label}
            placeholder={field.placeholder || "Enter phone number"}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            fullWidth
            helperText={field.help_text || ""}
          />
        );

      case 'file':
        return (
          <Box>
            <Typography variant="subtitle2" fontWeight={500} sx={{ color: '#1e293b', mb: 1 }}>
              {field.label}
              {field.required && (
                <Typography component="span" sx={{ color: '#ef4444', ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>
            <CustomInput
              type="file"
              onChange={(e) => handleInputChange(field.id, e.target.files[0])}
              required={field.required}
              fullWidth
              helperText={field.help_text || ""}
            />
          </Box>
        );

      default:
        return (
          <CustomInput
            type="text"
            label={field.label}
            placeholder={field.placeholder || "Enter text"}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
            fullWidth
            helperText={field.help_text || ""}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" variant="filled">
          There was an error loading the form. Please try again.
        </Alert>
      </Container>
    );
  }

  const sections = formStructure?.sections || [];

  return (
    <Container maxWidth="lg">
      <Box mb={1}>
        <Typography variant="h4" fontWeight={600} sx={{ color: '#0f172a' }}>
          About Me
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Please fill in your personal information
        </Typography>
        {submissionId && (
          <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
            ✓ Last saved: {new Date().toLocaleString()}
          </Typography>
        )}
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #e2e8f0',
              bgcolor: '#f8fafc',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.85rem',
                py: 2,
                px: 3,
                minHeight: '48px',
                color: '#64748b',
                '&.Mui-selected': {
                  color: '#6366f1',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#6366f1',
                height: 3,
              },
            }}
          >
            {sections.map((section, index) => (
              <Tab 
                key={section.id} 
                label={section.title || `Section ${index + 1}`} 
                iconPosition="start"
              />
            ))}
          </Tabs>

          {sections.map((section, index) => (
            <Box
              key={section.id}
              role="tabpanel"
              hidden={activeTab !== index}
              id={`tabpanel-${index}`}
              aria-labelledby={`tab-${index}`}
            >
              {activeTab === index && (
                <CardContent sx={{ p: 3 }}>
                  {section.description && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      {section.description}
                    </Typography>
                  )}
                  
                  {section.fields?.map((field) => (
                    <Box key={field.id} sx={{ mb: 3 }}>
                      {renderField(field)}
                    </Box>
                  ))}

                  {(!section.fields || section.fields.length === 0) && (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body2" color="textSecondary">
                        No fields in this section
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              )}
            </Box>
          ))}
        </Paper>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <Button
            variant="outlined"
            onClick={() => setFormData({})}
            disabled={postMutation.isLoading}
            startIcon={<ClearIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc',
              },
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={postMutation.isLoading}
            startIcon={postMutation.isLoading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(79, 70, 229, 0.4)',
              },
              '&:disabled': {
                background: '#94a3b8',
              },
            }}
          >
            {postMutation.isLoading ? 'Saving...' : submissionId ? 'Update' : 'Submit'}
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default AboutMe;